import React, { useState, useRef, useEffect } from "react";

// Enhanced Image component with Intersection Observer for better lazy loading performance
const Image = (props) => {
  const { 
    src, 
    alt, 
    name, 
    className = '', 
    style, 
    height, 
    width, 
    loading = "lazy", 
    decoding = "async",
    fetchpriority = "auto"
  } = props;

  const [isInView, setIsInView] = useState(loading === "eager" || fetchpriority === "high");
  const imgRef = useRef(null);

  // Intersection Observer for better lazy loading performance
  useEffect(() => {
    if (isInView || loading === "eager") return;

    // Fallback for browsers that don't support Intersection Observer
    if (typeof IntersectionObserver === "undefined") {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: "50px", // Start loading 50px before image enters viewport
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.disconnect();
      }
    };
  }, [isInView, loading]);

  // Use the split method to divide the path into an array based on the '/'
  const pathArray = src.split("/");
  // Get the last element of the array, which represents the file name
  const lastPathName = pathArray[pathArray.length - 1];

  return (
    <>
      {isInView ? (
        <img
          ref={imgRef}
          src={src}
          alt={alt || lastPathName}
          name={name || lastPathName}
          className={`${className} img-fluid`}
          style={style}
          width={width}
          height={height}
          loading={loading}
          decoding={decoding}
          fetchpriority={fetchpriority}
        />
      ) : (
        <div
          ref={imgRef}
          className={`${className} img-fluid`}
          style={{
            ...style,
            width: width || "100%",
            height: height || "auto",
            backgroundColor: "#f0f0f0",
            display: "inline-block",
          }}
          aria-label={alt || lastPathName}
        />
      )}
    </>
  );
};

export default Image;
